/**
 * Kink Search Component
 * 
 * Search bar with filters for browsing kinks.
 * Includes:
 * - Text search input
 * - Category filter dropdown
 * - Weirdness range filter
 * - Sort options
 * 
 * Props:
 * - onSearch: Callback with search parameters
 * - categories: Array of available categories
 * - loading: Whether search is in progress
 */

import React, { useState, useEffect } from 'react';
import { SlideUp, Spinner } from './Animations';

function KinkSearch({ onSearch, categories = [], loading = false, compact = false, autoFocus = false }) {
  // Search state
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [minKinkiness, setMinKinkiness] = useState('');
  const [maxKinkiness, setMaxKinkiness] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [showFilters, setShowFilters] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      triggerSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, category, minKinkiness, maxKinkiness, sortBy, sortOrder]);

  // Trigger search with current parameters
  const triggerSearch = () => {
    onSearch({
      search,
      category,
      minWeirdness: minKinkiness || undefined,
      maxWeirdness: maxKinkiness || undefined,
      sortBy,
      sortOrder
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setMinKinkiness('');
    setMaxKinkiness('');
    setSortBy('name');
    setSortOrder('ASC');
  };

  const hasActiveFilters = category || minKinkiness || maxKinkiness;

  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      {/* Main Search Bar */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search kinks by name, description, or appeal..."
          className="input pl-12 pr-24"
          autoFocus={autoFocus}
        />

        {/* Search Icon */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-500">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Filter Toggle Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`
            absolute right-3 top-1/2 -translate-y-1/2 
            px-3 py-1.5 text-sm font-medium
            transition-all duration-200
            ${showFilters || hasActiveFilters
              ? 'bg-wine-500/20 text-wine-400'
              : 'bg-dark-700/50 text-dark-400 hover:text-dark-200'
            }
          `}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-wine-500" />
            )}
          </span>
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <SlideUp className="glass-card p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Weirdness Range */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-dark-400 mb-2">
                Kinkiness Range
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={minKinkiness}
                  onChange={(e) => setMinKinkiness(e.target.value)}
                  className="input flex-1"
                >
                  <option value="">Min</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span className="text-dark-500">to</span>
                <select
                  value={maxKinkiness}
                  onChange={(e) => setMaxKinkiness(e.target.value)}
                  className="input flex-1"
                >
                  <option value="">Max</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sort Options */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-2">
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input flex-1"
                >
                  <option value="name">Name</option>
                  <option value="weirdnessScore">Kinkiness</option>
                  <option value="createdAt">Date Added</option>
                  <option value="category">Category</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
                  className="btn-secondary px-3"
                  title={sortOrder === 'ASC' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'ASC' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-dark-700/50">
              <button
                onClick={clearFilters}
                className="text-sm text-wine-400 hover:text-wine-300 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
        </SlideUp>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="flex items-center justify-center py-2">
          <Spinner size="sm" color="wine" />
          <span className="ml-2 text-sm text-dark-400">Searching...</span>
        </div>
      )}
    </div>
  );
}

export default KinkSearch;
